/**
 * useTierFilteredNavItems Hook
 * TD-014: Frontend Route Guards - Tier-Based Navigation Filtering
 * FIX-055: Sidebar navigation race condition on initial login
 *
 * Filters navigation items based on the current user's tier level.
 * Items with minTier higher than the user's tier are hidden from the sidebar.
 * Groups with no visible children are also hidden.
 *
 * FIX-055 / REQ-R2-FE-3: When the user profile is not yet hydrated (tierLevel is
 * 0, or user is null while isAuthenticated is true) the gate CANNOT evaluate its
 * input, so it DENIES — the hook returns an empty list and the sidebar renders
 * nothing for that brief window.
 *
 * The original FIX-055 did the opposite: it returned the FULL unfiltered set to
 * avoid an empty sidebar. Route guards still blocked access, but for roughly a
 * second after login a tier-1 user could see Company / Analytics / Behavioral /
 * Employees / Team in the sidebar. QA screenshotting first paint filed that as a
 * Critical access-control defect against 19 test cases.
 *
 * Per `.claude/rules/gate-fail-loud.md`, a restriction control that cannot
 * evaluate must deny, not permit. Showing MORE than a user is entitled to is a
 * security-class failure; showing an empty nav for one render is cosmetic.
 * `isProfileLoaded` is exposed so a consumer can render a loading affordance.
 */
"use client";

import * as React from "react";
import { useAuth } from "@apal/shared-auth";
import type { ApalNavItemConfig } from "@/config/nav";
import type { NavItemConfig } from "@/types/nav";

export interface UseTierFilteredNavItemsResult {
	/** Filtered navigation items based on user tier level */
	filteredItems: NavItemConfig[];
	/** Whether auth is still loading */
	isLoading: boolean;
	/** Whether the user profile (with tier level) is fully loaded */
	isProfileLoaded: boolean;
}

/**
 * Hook to filter navigation items based on user's tier level.
 *
 * @param items - Array of navigation items with optional minTier requirements
 * @returns Object with filtered items, loading state, and profile loaded state
 *
 * @example
 * ```tsx
 * const { filteredItems, isLoading, isProfileLoaded } = useTierFilteredNavItems(apalNavItems);
 * return <SideNav items={filteredItems} />;
 * ```
 */
export function useTierFilteredNavItems(
	items: ApalNavItemConfig[]
): UseTierFilteredNavItemsResult {
	const { isAuthenticated, isLoading, user } = useAuth();

	const isProfileLoaded = Boolean(
		isAuthenticated && user && user.tierLevel >= 1
	);

	const filteredItems = React.useMemo((): NavItemConfig[] => {
		// Still loading auth state - return empty
		if (isLoading) {
			return [];
		}

		// Not authenticated - return empty
		if (!isAuthenticated) {
			return [];
		}

		// FIX-055 / REQ-R2-FE-3: Authenticated but profile not yet hydrated
		// (user is null, or tierLevel is still 0). The tier gate cannot evaluate,
		// so it FAILS CLOSED and shows nothing rather than leaking tier-gated
		// items during the auth transition. See the file header for the QA
		// false-failure this prevents.
		if (!user || user.tierLevel < 1) {
			return [];
		}

		const userTier = user.tierLevel;

		function filterByTier(navItems: ApalNavItemConfig[]): NavItemConfig[] {
			return navItems.reduce<NavItemConfig[]>((acc, item) => {
				// Check if user meets tier requirement
				if (item.minTier !== undefined && userTier < item.minTier) {
					return acc;
				}

				// If item has children, filter them recursively
				if (item.items && item.items.length > 0) {
					const filteredChildren = filterByTier(item.items);

					// If this is a group header (no href) and all children are filtered out, skip it
					if (!item.href && filteredChildren.length === 0) {
						return acc;
					}

					acc.push({
						...item,
						items: filteredChildren.length > 0 ? filteredChildren : undefined,
					});
				} else {
					acc.push(item);
				}

				return acc;
			}, []);
		}

		return filterByTier(items);
	}, [items, isLoading, isAuthenticated, user]);

	return {
		filteredItems,
		isLoading,
		isProfileLoaded,
	};
}
